// OnboardingWizard.tsx - Shorter 3-step onboarding
import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { ProgressBar, Container, Button } from "react-bootstrap";
import { SegmentStep } from "./Steps/SegmentStep";
import { VolumeStep } from "./Steps/VolumeStep";
import { TeamStep } from "./Steps/TeamStep";
import { saveSurveyResults } from "src/docuclipper/api";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { fetchUser } from "src/redux/reducers/User";
import "./OnboardingWizard.scss";

interface FormValues {
  // Step 1: Segment
  segment: string;
  "segment-Comment"?: string;

  // Step 2: Volume & Frequency (IMPORTANT for plan offerings)
  document_volume: string;
  document_frequency: string;
  use_case_type: string; // "one-off-large" | "one-off-small" | "ongoing"

  // Step 3: Team
  teammates: { label: string; value: string }[];
}

const validationSchema = [
  // Step 1: Segment validation
  Yup.object({
    segment: Yup.string().required("Please select your role"),
    "segment-Comment": Yup.string().when("segment", {
      is: "Other",
      then: Yup.string().required("Please specify your role"),
      otherwise: Yup.string(),
    }),
  }),

  // Step 2: Volume & Frequency validation (document_volume is optional now)
  Yup.object({
    use_case_type: Yup.string().required("Please select your use case type"),
    document_volume: Yup.string().optional(),
  }),

  // Step 3: Team validation (optional)
  Yup.object({
    teammates: Yup.array().of(Yup.string()),
  }),
];

const initialValues: FormValues = {
  segment: "",
  document_volume: "",
  document_frequency: "",
  use_case_type: "",
  teammates: [],
};

const STEPS = [
  {
    title: "Tell us about yourself",
    description: "Help us understand your role and needs",
  },
  {
    title: "Your Document Needs",
    description: "Help us recommend the perfect plan for your situation",
  },
  {
    title: "Build Your Team",
    description: "Invite team members to collaborate (optional)",
  },
];

export const OnboardingWizard: React.FC = () => {
  const [step, setStep] = useState(0);
  const totalSteps = STEPS.length;
  const history = useHistory();
  const dispatch = useDispatch();

  const handleSubmit = async (values: FormValues) => {
    const SURVEY_SHOWN_KEY = "docuclipper.survey.onboarding3.shown";

    saveSurveyResults("onboarding3", values)
      .then(() => {
        localStorage.setItem(SURVEY_SHOWN_KEY, "true");
        // Redirect based on use case type and volume to show appropriate plan
        // if (values.use_case_type === "one-off") {
        //   // Check volume for one-off projects
        //   const volume = values.document_volume;
        //   if (
        //     volume === "201-500" ||
        //     volume === "501-1000" ||
        //     volume === "1000+"
        //   ) {
        //     history.push("/extractdata?plan_suggestion=enterprise-project");
        //   } else {
        //     history.push("/extractdata?plan_suggestion=starter");
        //   }
        // } else if (values.use_case_type === "ongoing") {
        //   history.push("/extractdata?plan_suggestion=monthly");
        // } else {
        //   history.push("/extractdata");
        // }
        history.push("/extractdata");
        dispatch(fetchUser());
      })
      .catch((err) => {
        console.error("Failed to save survey results:", err);
        history.push("/extractdata");
      });
  };

  const handleSkipSurvey = () => {
    const SURVEY_SHOWN_KEY = "docuclipper.survey.onboarding3.shown";
    localStorage.setItem(SURVEY_SHOWN_KEY, "true");
    history.push("/extractdata");
    dispatch(fetchUser());
  };

  return (
    <Container className="py-5 onboarding-wizard" style={{ maxWidth: "700px" }}>
      <h2 className="text-center mb-2">{STEPS[step].title}</h2>
      <p className="text-center text-muted mb-4">{STEPS[step].description}</p>
      <ProgressBar now={(step + 1) * (100 / totalSteps)} className="mb-4" />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema[step]}
        onSubmit={(values, actions) => {
          if (step < totalSteps - 1) {
            setStep(step + 1);
          } else {
            handleSubmit(values);
          }
        }}
      >
        {(formik) => (
          <Form>
            {step === 0 && <SegmentStep />}
            {step === 1 && <VolumeStep />}
            {step === 2 && <TeamStep />}

            <div className="d-flex justify-content-between mt-4">
              <div>
                {step > 0 && (
                  <Button variant="secondary" onClick={() => setStep(step - 1)}>
                    Previous
                  </Button>
                )}
              </div>
              <div>
                {step === 0 && (
                  <Button
                    variant="link"
                    onClick={handleSkipSurvey}
                    className="me-2 btn--skip-for-now"
                  >
                    Skip for now
                  </Button>
                )}
                {step < totalSteps - 1 ? (
                  <Button type="submit" variant="primary">
                    Continue
                  </Button>
                ) : (
                  <Button type="submit" variant="success">
                    Get Started
                  </Button>
                )}
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </Container>
  );
};
